import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canRaisePO, canApprovePO, formatINR, formatDate, hasCapability } from "@/lib/permissions";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FilePlus, Search, ShoppingCart, ChevronRight, ChevronDown, Package, Coffee, Wrench } from "lucide-react";

const STATUS_META = {
  draft: { label: "Draft", cls: "border-slate-300 text-slate-600 bg-slate-50" },
  pending_approval: { label: "Pending Approval", cls: "border-amber-300 text-amber-800 bg-amber-50" },
  approved: { label: "Approved", cls: "border-emerald-300 text-emerald-800 bg-emerald-50" },
  rejected: { label: "Rejected", cls: "border-red-300 text-red-700 bg-red-50" },
  issued: { label: "Issued", cls: "border-blue-300 text-blue-800 bg-blue-50" },
  partially_received: { label: "Partial Receipt", cls: "border-teal-300 text-teal-800 bg-teal-50" },
  received: { label: "Received", cls: "border-indigo-300 text-indigo-800 bg-indigo-50" },
  closed: { label: "Closed", cls: "border-slate-400 text-slate-700 bg-slate-100" },
  cancelled: { label: "Cancelled", cls: "border-rose-400 text-rose-700 bg-rose-50" },
  superseded: { label: "Superseded", cls: "border-orange-300 text-orange-800 bg-orange-50" },
};

// Sep 2026 — each PO type is a clickable section (tab); the list below shows
// only that type. Tabs carry live counts that respect the search/status filters.
const PO_TYPE_TABS = [
  { key: "asset_procurement", label: "Fixed Asset (CG)", desc: "Capital goods purchases", icon: Package },
  { key: "consumables_purchase", label: "Consumables (CON)", desc: "Consumable purchases", icon: Coffee },
  { key: "maintenance_spare", label: "Machine Spares & Jigs (SPR)", desc: "Spares, jigs & repairs", icon: Wrench },
];

function POCells({ p, indent = false }) {
  return (
    <>
      <td className={`px-4 py-3 font-mono text-xs font-semibold text-[#7C1D3A] ${indent ? "pl-10" : ""}`}>
        <span className="inline-flex items-center gap-1">
          {indent && <span className="text-slate-300 select-none">↳</span>}
          {p.po_number}
          {p.revision_number > 0 && (
            <span className="text-[10px] font-semibold px-1 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200" title={`Revision ${p.revision_number}`}>REV{p.revision_number}</span>
          )}
        </span>
      </td>
      <td className="px-4 py-3 text-slate-600">{formatDate(p.po_date)}</td>
      <td className="px-4 py-3">
        <div className="font-medium text-slate-900">{p.vendor_name}</div>
        {p.vendor_gstin && <div className="text-xs text-slate-500 font-mono">{p.vendor_gstin}</div>}
      </td>
      <td className="px-4 py-3 text-right font-semibold text-slate-900">
        {p.currency && p.currency !== "INR" ? (
          <>
            <span className="text-xs font-normal text-slate-500 mr-1">{p.currency}</span>
            {Number(p.grand_total || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </>
        ) : formatINR(p.grand_total)}
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline" className={STATUS_META[p.status]?.cls || ""}>
          {STATUS_META[p.status]?.label || p.status}
        </Badge>
      </td>
      <td className="px-4 py-3 text-slate-600 text-xs">{p.created_by_name || "—"}</td>
    </>
  );
}

function FragmentRows({ head, children_, isOpen, hasHistory, onToggle, onOpen }) {
  return (
    <>
      <tr
        className="hover:bg-slate-50 cursor-pointer"
        onClick={() => onOpen(head.id)}
        data-testid={`po-row-${head.po_number}`}
      >
        <td className="px-2 py-3 w-8 text-center" onClick={(e) => e.stopPropagation()}>
          {hasHistory ? (
            <button
              onClick={onToggle}
              className="text-slate-400 hover:text-[#7C1D3A] p-0.5 rounded"
              title={isOpen ? "Hide earlier revisions" : `Show ${children_.length} earlier revision${children_.length === 1 ? "" : "s"}`}
              data-testid={`po-expand-${head.po_number}`}
            >
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : null}
        </td>
        <POCells p={head} />
        <td className="px-4 py-3 text-right pr-4">
          {hasHistory && (
            <span
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#7C1D3A]"
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              role="button"
              title={isOpen ? "Hide earlier revisions" : `View ${children_.length} earlier revision${children_.length === 1 ? "" : "s"}`}
            >
              {children_.length} earlier rev{children_.length === 1 ? "" : "s"}
            </span>
          )}
        </td>
      </tr>
      {isOpen && children_.map((c) => (
        <tr
          key={c.id}
          className="bg-orange-50/30 hover:bg-orange-50/60 cursor-pointer text-slate-500"
          onClick={() => onOpen(c.id)}
          data-testid={`po-row-${c.po_number}`}
        >
          <td className="px-2 py-2 w-8"></td>
          <POCells p={c} indent />
          <td></td>
        </tr>
      ))}
    </>
  );
}

export default function PurchaseOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeType, setActiveType] = useState("asset_procurement");
  // Which revision chains have their history expanded
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (rootId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId); else next.add(rootId);
      return next;
    });
  };

  const load = async () => {
    try {
      const { data } = await api.get("/purchase-orders");
      setPos(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load POs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const matchesFilters = (p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (q.trim()) {
      const hay = `${p.po_number} ${p.vendor_name} ${p.notes || ""}`.toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    return true;
  };

  // Group POs by revision chain. Head = the "live" PO in each chain (the one
  // that isn't superseded); siblings = older superseded revisions. When the
  // user explicitly filters by "superseded" status we bypass grouping so they
  // can browse the archived revisions directly.
  const groups = useMemo(() => {
    if (statusFilter === "superseded") {
      // Flat view — every superseded PO surfaced individually
      return pos.filter(matchesFilters).map((p) => ({ head: p, children: [], rootId: p.id }));
    }
    const map = new Map();
    for (const p of pos) {
      const rootId = p.revision_root || p.id;
      if (!map.has(rootId)) map.set(rootId, []);
      map.get(rootId).push(p);
    }
    const out = [];
    for (const [rootId, chain] of map.entries()) {
      // sort chain ascending by revision_number, so head = last (highest rev)
      chain.sort((a, b) => (a.revision_number || 0) - (b.revision_number || 0));
      // Pick head = the one NOT superseded (usually the highest rev). If all
      // superseded (rare), still take the highest rev.
      let head = chain.find((p) => p.status !== "superseded");
      if (!head) head = chain[chain.length - 1];
      const children = chain.filter((p) => p.id !== head.id);
      // Filter: keep group if head OR any child matches. Prefer showing head as
      // the visible row; if only children match, we still show head (as context)
      // and force-expand the group.
      const headMatches = matchesFilters(head);
      const anyChildMatches = children.some(matchesFilters);
      if (!headMatches && !anyChildMatches) continue;
      out.push({
        head,
        children: children.slice().reverse(), // newest superseded first when expanded
        rootId,
        forceExpanded: !headMatches && anyChildMatches,
      });
    }
    // Sort groups by head date desc (matches previous list ordering)
    out.sort((a, b) => (b.head.created_at || "").localeCompare(a.head.created_at || ""));
    return out;
  }, [pos, q, statusFilter]);

  // Per-type buckets: counts drive the tab badges; the active tab's list shows below.
  const byType = useMemo(() => {
    const m = { asset_procurement: [], consumables_purchase: [], maintenance_spare: [] };
    for (const g of groups) {
      (m[g.head.po_type] ||= []).push(g);
    }
    return m;
  }, [groups]);

  const activeGroups = byType[activeType] || [];
  const activeTab = PO_TYPE_TABS.find((t) => t.key === activeType);
  const totalGroups = groups.length;
  const supersededCount = pos.filter((p) => p.status === "superseded").length;

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Procurement</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Purchase Orders</h1>
          <p className="text-slate-500 mt-1 text-sm">
            {pos.length} POs · {totalGroups} active
            {supersededCount > 0 && <span> · <span className="text-orange-700">{supersededCount} superseded</span> (tap ▶ to expand)</span>}
          </p>
        </div> {hasCapability(user, "raise_po") && (
          <Button
            onClick={() => navigate("/purchase-orders/new")}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
            data-testid="new-po-btn"
          >
            <FilePlus className="w-4 h-4 mr-2" /> Raise PO
          </Button>
        )}
      </div>

      {/* PO type sections — click a type to see its list */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="po-type-tabs">
        {PO_TYPE_TABS.map((t) => {
          const Icon = t.icon;
          const active = activeType === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveType(t.key)}
              className={`text-left rounded-xl border p-4 transition-all ${
                active
                  ? "border-[#7C1D3A] bg-[#7C1D3A]/[0.04] shadow-sm ring-1 ring-[#7C1D3A]/30"
                  : "border-slate-200 bg-white hover:border-[#7C1D3A]/40 hover:shadow-sm"
              }`}
              data-testid={`po-tab-${t.key}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${active ? "bg-[#7C1D3A] text-white" : "bg-slate-100 text-slate-500"}`}>
                  <Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" />
                </span>
                <span className={`text-xl font-bold font-heading ${active ? "text-[#7C1D3A]" : "text-slate-700"}`} data-testid={`po-tab-count-${t.key}`}>
                  {(byType[t.key] || []).length}
                </span>
              </div>
              <div className={`mt-2 font-semibold text-sm ${active ? "text-[#7C1D3A]" : "text-slate-800"}`}>{t.label}</div>
              <div className="text-[11px] text-slate-500">{t.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder={`Search ${activeTab?.label || ""} POs — number, vendor...`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="po-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="po-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Active type's list */}
      <Card>
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="font-semibold text-slate-900 text-sm" data-testid="po-active-section-title">{activeTab?.label}</div>
          <div className="text-[11px] text-slate-500">{activeGroups.length} PO{activeGroups.length === 1 ? "" : "s"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                <th className="px-2 py-3 w-8"></th>
                <th className="px-4 py-3">PO Number</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Vendor</th>
                <th className="px-4 py-3 text-right">Grand Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Raised By</th>
                <th className="px-4 py-3 text-right">Revisions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">Loading...</td></tr>
              ) : activeGroups.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center">
                  <ShoppingCart className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <div className="text-slate-500" data-testid="po-section-empty">No {activeTab?.label} POs yet</div>
                  {hasCapability(user, "raise_po") && (
                    <Button onClick={() => navigate("/purchase-orders/new")} variant="link" className="text-[#7C1D3A]">
                      Raise your first PO →
                    </Button>
                  )}
                </td></tr>
              ) : activeGroups.map((g) => {
                const isOpen = g.forceExpanded || expanded.has(g.rootId);
                const hasHistory = g.children.length > 0;
                return (
                  <FragmentRows
                    key={g.rootId}
                    head={g.head}
                    children_={g.children}
                    isOpen={isOpen}
                    hasHistory={hasHistory}
                    onToggle={() => toggleExpanded(g.rootId)}
                    onOpen={(id) => navigate(`/purchase-orders/${id}`)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
